import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { firestore } from '../firebase';
import type { PartnerProfile } from '../types';

/**
 * Subscribes to the signed-in partner's own partnerProfiles/{uid} doc — the
 * same collection the Partner Dashboard website reads/writes (see
 * partnerProfileStore.js there). Only meaningful once `useProfile()` has
 * resolved role === 'partner'; pass null otherwise to skip the subscription.
 */
export function usePartnerProfile(uid: string | null) {
  const [partnerProfile, setPartnerProfile] = useState<PartnerProfile | null>(null);
  // Tracks which uid the current `partnerProfile` value actually belongs to,
  // rather than a plain boolean — a boolean set via setLoading(false) inside
  // the effect only updates AFTER this render commits, so on the exact
  // render where `uid` first flips from null to a real value, a plain
  // boolean would still read its stale prior value (false) for one render,
  // making `loading` wrongly read false while `partnerProfile` is still
  // null/stale for the new uid. Deriving `loading` in the render body from
  // "does resolvedForUid match the uid we were just asked about" can never
  // be stale this way.
  const [resolvedForUid, setResolvedForUid] = useState<string | null>(null);
  const loading = Boolean(uid) && resolvedForUid !== uid;

  useEffect(() => {
    if (!uid) {
      setPartnerProfile(null);
      setResolvedForUid(null);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      doc(firestore, 'partnerProfiles', uid),
      snapshot => {
        setPartnerProfile(snapshot.exists() ? (snapshot.data() as PartnerProfile) : null);
        setResolvedForUid(uid);
      },
      error => {
        console.warn('[Tonyte][PartnerProfile] subscription failed', error);
        setPartnerProfile(null);
        // Still mark this uid as "resolved" (to null) — an error is a
        // terminal outcome for this attempt, not an excuse to keep the
        // caller stuck in a permanent loading state.
        setResolvedForUid(uid);
      },
    );

    return () => unsubscribe();
  }, [uid]);

  return { partnerProfile, loading };
}
